import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Nav } from 'react-bootstrap';
import EntityRevenueChartAndTable from './EntityRevenueChartAndTable';
import EntityExpensesChartAndTable from './EntityExpensesChartAndTable';
import EntitySurpDefOverview from './EntitySurpDefOverview';
import EntityHighestPaidChartAndTable from './EntityHighestPaidChartAndTable';
import EntityComplianceReportOverview from './EntityComplianceReportOverview';
import EntityVendorHighestPaidOverview from './EntityVendorHighestPaidOverview';
import EntityBatchListOverview from './EntityBatchListOverview';
import EntityReportsOverview from './EntityReportsOverview';
import EntityDataQualityOverview from './EntityDataQualityOverview';
import EntityDetailsPage from './EntityDetails';
import EntityDetailsOverview from './EntityDetailsOverview'; // Added new version
import EntityPopulationOverview from './EntityPopulationOverview';
import EntityTotalRevenueOverview from './EntityTotalRevenueOverview';
import type { TotalRevenueLong } from './EntityTotalRevenueOverview';
import EntityTotalExpenditureOverview from './EntityTotalExpenditureOverview';
import type { TotalExpenditureLong } from './EntityTotalExpenditureOverview';
import EntityUnrestrictedCashOverview from './EntityUnrestrictedCashOverview';
import type { UnrestrictedCashLong } from './EntityUnrestrictedCashOverview';
import KpiCard from './KpiCard';
import OverviewSection from './OverviewSection';
import FeatureNav from './FeatureNav';
import FooterSection from './FooterSection';
import Loader from './Loader';
import { SingleSelectAsync } from './SIngleSelectAsync';
import { ComplianceReportApi } from 'types/complianceReport';
import {
  EntityDetailsInterface,
} from 'types/entityDetails';
import BreadCrumbs from './BreadCrumbs';
import { prodApiEndpointUrl, stagingApiEndpointUrl, devApiEndpointUrl } from 'types/apiEndpoints';

interface ChartEntity {
  entity_id: number;
  entity_name: string;
  fiscal_year: number;
  agg_type: string;
  agg1: string;
  net_amount: number;
}

interface SurpDefEntity {
  entity_id: number;
  entity_name: string;
  fiscal_year: number;
  net_revenue: number;
  net_expense: number;
  surdef: number;
}

interface AllEntity {
  name: string;
  id: number;
}

interface HighestPaidEmployeeByEntity {
  entity_id: number;
  emp_rank: number;
  fiscal_year: number;
  entity_name: string;
  employee_name: string;
  title: string;
  org1: string;
  wages: number;
  benefits: number;
  total: number;
}

interface HighestPaidVendors {
  fiscal_year: number;
  entity_name: string;
  vendor_name: string;
  vendor_beg_name_id: number;
  net_amount: number;
}

interface BatchSummary {
  entity_id: number;
  entity_name: string;
  batch_id: string;
  file_status: string;
  govt_lvl: string;
  min_posting_date: Date;
  max_posting_date: Date;
  record_count: number;
  net_amount: number;
  net_amount_rv: number;
  net_amount_rx: number;
  net_amount_py: number;
  fiscal_years: string;
  types: string;
  quarters: string;
  sf_id: string;
  upload_date: string;
  file_name: string;
}

export interface EntityScores {
  entity_id: number;
  entity_name: string;
  govt_lvl: string;
  uniqueness_score: number;
  reasonable_score: number;
  yearly_change_score: number;
  yearly_no_cap_change_score: number;
  reversal_score: number;
  timeliness_score: number;
  detailed_score: number;
  uca_score: number;
  emp_comp_score: number;
  consistency_cafr_score: number;
  consistency_usbe_score: number;
}

interface entityDetails {
  entity_id: string;
  entity_name: string;
  entity_government_type : string, 
  entity_government_class: string, 
  entity_name_snakecase: string, 
  sf_name: string, 
  sf_id: string, 
  tax_commission_id: string, 
  ein: string, 
  finet_vendor_ids: string, 
  transparency_id: string, 

  sf_reports_to_transparent_utah : boolean, 
  sf_reports_to_privacy: boolean, 
  sf_reports_to_local_gov: boolean, 
  sf_reports_to_treasurer: boolean, 

  public_infrastructure_district: boolean, 
  infrastructure_financing_district: boolean, 
  special_project_entity: boolean, 
  governmental_non_profits: boolean, 
  fs_reports_with_parent: boolean, 
  fee_assessing_entity: boolean, 
  component_unit_type: boolean, 
  tax_holding_county: boolean, 

  created_in_salesforce_date: string, //Date, 
  creation_date: string, //Date, 

  website: string, 
  entity_description: string, 
  entity_purpose: string, 

  form_of_government: string, 
  number_of_governing_body_seats: string, 
  external_auditors: string, 

  billing_address: string, 
  billing_street: string, 
  billing_city: string, 
  billing_country: string, 
  billing_postal_code: string, 
  billing_state: string, 

  shipping_address: string, 
  shipping_street: string, 
  shipping_city: string, 
  shipping_postal_code: string, 
  shipping_state: string, 
  shipping_country: string, 
  
  fiscal_year_begins : string, //Date, 
  fiscal_year_end : string, //Date, 
  fiscal_year_change_effective_date : string, //Date, 
  fiscal_year_has_changed : string, //Date, 
  transparency_report_expense_revenue_start_date : string, //Date, 
  transparency_report_expense_revenue_end_date : string, //Date, 
  transparency_report_wage_start_date : string, //Date, 
  transparency_report_wage_end_date : string, //Date, 

  entity_status: string, 
  hold_funds: boolean, 
  hold_deposit_and_investment: boolean, 
  suspend_funds: boolean, 
  suspend_funds_deposit_and_investment: boolean, 
  freeze_funds: boolean, 
  freeze_funds_deposit_and_investment: boolean, 
  transparency_delinquency: boolean, 
  transparency_hold: boolean, 
  transparency_suspend: boolean
}

interface EntityPopulation {
  census_population_long_id: string;
  entity_id: string;
  entity_name: string;
  census_year: number;
  census_population: number;
  census_summary_level: string;
  census_state_fips_code: string;
  census_county_fips_code: string;
  census_place_fips_code: string;
  census_area_name: string;
}

const EntityOverviewDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchParams, setSearchParams] = useSearchParams();
  // const [selectedEntityId, setSelectedEntityId] = useState<number>(0);
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null); // Added option for null value
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [entity, setEntity] = useState<string | null>(null);
  const [allEntities, setAllEntities] = useState<AllEntity[]>([]);
  const [fiscalYear, setFiscalYear] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingSurpDef, setLoadingSurpDef] = useState<boolean>(false);
  const [loadingExpenses, setLoadingExpenses] = useState<boolean>(false);
  const [loadingRevenue, setLoadingRevenue] = useState<boolean>(false);
  const [loadingHighestPaid, setLoadingHighestPaid] = useState<boolean>(false);
  const [loadingCompliance, setLoadingCompliance] = useState<boolean>(false);
  const [loadingVendorSearch, setLoadingVendorSearch] = useState<boolean>(false);
  const [loadingEntityDetails, setLoadingEntityDetails] = useState<boolean>(false);
  const [entityHeaderLoading, setEntityHeaderLoading] = useState<boolean>(false); // Added Entity Header loading component
  const [surpDefData, setSurpDefData] = useState<SurpDefEntity[]>([]);
  const [expensesFundData, setExpensesFundData] = useState<ChartEntity[]>([]);
  const [expensesOrgData, setExpensesOrgData] = useState<ChartEntity[]>([]);
  const [expensesCatData, setExpensesCatData] = useState<ChartEntity[]>([]);
  const [revenueFundData, setRevenueFundData] = useState<ChartEntity[]>([]);
  const [revenueOrgData, setRevenueOrgData] = useState<ChartEntity[]>([]);
  const [revenueCatData, setRevenueCatData] = useState<ChartEntity[]>([]);
  const [highestPaidData, setHighestPaidData] = useState<HighestPaidEmployeeByEntity[]>([]);
  const [complianceData, setComplianceData] = useState<ComplianceReportApi[]>([]);
  const [vendorSearchData, setVendorSearchData] = useState<HighestPaidVendors[]>([]);
  const [batchSummaryData, setBatchSummaryData] = useState<BatchSummary[]>([]);
  const [loadingReports, setLoadingReports] = useState<boolean>(false);
  const [reportDocs, setReportDocs] = useState<any[]>([]);
  const [entityDetailsData, setEntityDetailsData] = useState<entityDetails[]>([]); // Potentially get rid of this if it can be fully replaced by entityDetailsDataClean
  const [entityDetailsDataClean, setEntityDetailsDataClean] = useState<EntityDetailsInterface[]>([]); // Added to help fix issues with data loading order
  const [entityDataQualityData, setEntityDataQualityData] = useState<EntityScores[]>([]);
  const [entityOverviewHeaderData, setEntityOverviewHeaderData] = useState<entityDetails[]>([]);
  const [entityPopulationData, setEntityPopulationData] = useState<EntityPopulation[]>([]);
  const [totalRevenueLongData, setTotalRevenueLongData] = useState<TotalRevenueLong[]>([]);
  const [loadingRevenueTrends, setLoadingRevenueTrends] = useState<boolean>(false);
  const [totalExpenditureLongData, setTotalExpenditureLongData] = useState<TotalExpenditureLong[]>([]);
  const [loadingExpenditureTrends, setLoadingExpenditureTrends] = useState<boolean>(false);
  const [unrestrictedCashLongData, setUnrestrictedCashLongData] = useState<UnrestrictedCashLong[]>([]);
  const [loadingCashTrends, setLoadingCashTrends] = useState<boolean>(false);

  // To test against my ntgenix-dev backend locally, swap stagingApiEndpointUrl for devApiEndpointUrl on the line below.
  // Swap it back before pushing.
  /* const [apiEndpoint] = useState<string>(() => {
    return window.location.hostname === "transparent.utah.gov" ? prodApiEndpointUrl : stagingApiEndpointUrl;
  }); 
 */
  const [apiEndpoint] = useState<string>(() => {
  const hostname = window.location.hostname;
  if (hostname === "transparent.utah.gov") return prodApiEndpointUrl;
  if (hostname === "localhost" || hostname === "127.0.0.1") return devApiEndpointUrl;
  return stagingApiEndpointUrl;
  });

  const buildApiUrl = (apiEndpointUrl: string, searchParameter: string, encodedParam?: string) =>
    encodedParam
      ? `${apiEndpointUrl}${searchParameter}${encodedParam}`
      : `${apiEndpointUrl}${searchParameter}`;

  const currentYear: number = 2025; 

  const formatCurrency = (value: number) =>
      new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  
  const formatEntityString = (value: string) => {
    if (!value) return '';
    const parts = value.split('_');
    const capitalized = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
    return parts.map(capitalized).join(' ');
  };

  // Added to make vendor names title case on table
  const toTitleCase = (str: string): string => {
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getAllEntities = async (): Promise<AllEntity[]> => {
    const allEntitiesSearchString = `/?function=getAllEntities&parameter=%7B%7D`;
    try {
      const response = await fetch(buildApiUrl(apiEndpoint, allEntitiesSearchString));
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const entityArray = data
        .filter((e: { entity_id: number; entity_name: string | number }) => e.entity_id && e.entity_name)
        .map((e: { entity_id: number; entity_name: string | number }) => ({
          name: String(e.entity_name),
          id: e.entity_id,
        }));
      return entityArray;
    } catch (err) {
      console.error('Fetch error:', err);
      return [];
    }
  };

  // Updated - Fetch all entities and preload all tab data
  useEffect(() => {
    setLoading(true);
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchEntitiesAndAllData = async () => {
      try {
        const entities = await getAllEntities();
        setAllEntities(entities);

        if (entities.length > 0 && selectedEntityId === 0) {
          setSelectedEntities([entities[0].name]);
          setSelectedEntityId(entities[0].id);
        }

        if (!selectedEntityId) return;

        setLoadingSurpDef(true);
        setLoadingExpenses(true);
        setLoadingRevenue(true);
        setLoadingHighestPaid(true);
        setLoadingCompliance(true);
        setLoadingVendorSearch(true);
        setLoadingReports(true);
        setLoadingEntityDetails(true);
        setEntityHeaderLoading(true); // Added to help entity header start loading at the correct time 
        setLoadingRevenueTrends(true);
        setLoadingExpenditureTrends(true);
        setLoadingCashTrends(true);

        const encodedParam = encodeURIComponent(JSON.stringify({ entity_id: selectedEntityId }));

        const vendorEntityName =
          entities.find(entity => entity.id === selectedEntityId)?.name || entities[0]?.name || selectedEntities[0] || '';

        const vendorSearchEncodedParam = encodeURIComponent(JSON.stringify({ entity_name: vendorEntityName }));

        const highestPaidParam = {
          entity_id: selectedEntityId,
          fiscal_year: Number(fiscalYear ?? currentYear)
        };

        const highestPaidEncodedParam = encodeURIComponent(JSON.stringify(highestPaidParam));

        const payload = {
          entity_id: selectedEntityId
        }
        
        const entitySurpDefEncodedParam = encodeURIComponent(JSON.stringify(payload))

        // search strings for each api function
        const entitySurpDefSearchString = `/?function=getEntitySurplusDeficitForReact&parameter=`;
        const entityExpensesFundSearchString = `/?function=getEntityExpensesFunds&parameter=`;
        const entityExpensesOrgSearchString = `/?function=getEntityExpensesOrgs&parameter=`;
        const entityExpensesCatSearchString = `/?function=getEntityExpensesCats&parameter=`;
        const entityRevenueFundSearchString = `/?function=getEntityRevenueFunds&parameter=`;
        const entityRevenueOrgSearchString = `/?function=getEntityRevenueOrgs&parameter=`;
        const entityRevenueCatSearchString = `/?function=getEntityRevenueCats&parameter=`;
        const highestPaidEmployeesByEntitySearchString = `/?function=getHighestPaidEmployeesByEntityID&parameter=`;
        const complianceDataSearchString = `/?function=getComplianceData&parameter=`;
        const vendorDataSearchString = `/?function=getVendorSearchByEntity&parameter=`;
        const batchListSearchString = `/?function=getEntityBatches&parameter=`;
        const reportDocsSearchString = `/?function=getEntityReportDocuments&parameter=`;
        const dataQualitySearchString = `/?function=getDQIScoresRecentYearByEntityName&parameter=`;
        const entityDetailsSearchString = `/?function=getEntityDetails&parameter=`;
        const entityPopulationSearchString = `/?function=getEntityPopulationDetails&parameter=`;
        const totalRevenueLongSearchString = `/?function=getTotalRevenueLong&parameter=`;
        const totalExpenditureLongSearchString = `/?function=getTotalExpenditureLong&parameter=`;
        const unrestrictedCashLongSearchString = `/?function=getUnrestrictedCashLong&parameter=`;

        // Fetch all data in parallel
        // const fetchSurpDef = fetch(
        //   `${api_endpoint}/?function=getEntitySurplusDeficit&parameter=${encodedParam}`,
        //   { signal }
        // ).then(res => res.ok ? res.json() : []);

        // Updated to include updated function for React
        // const fetchSurpDef = fetch(
        //   `${api_endpoint}/?function=getEntitySurplusDeficitForReact&parameter=${encodedParam}`,
        //   { signal }
        // ).then(res => res.ok ? res.json() : []);

        // Updated version to fix issues with
        const fetchSurpDef = fetch(
          buildApiUrl(apiEndpoint, entitySurpDefSearchString, entitySurpDefEncodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchExpensesFund = fetch(
          buildApiUrl(apiEndpoint, entityExpensesFundSearchString, encodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchExpensesOrg = fetch(
          buildApiUrl(apiEndpoint, entityExpensesOrgSearchString, encodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchExpensesCat = fetch(
          buildApiUrl(apiEndpoint, entityExpensesCatSearchString, encodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchRevenueFunds = fetch(
          buildApiUrl(apiEndpoint, entityRevenueFundSearchString, encodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchRevenueOrgs = fetch(
          buildApiUrl(apiEndpoint, entityRevenueOrgSearchString, encodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchRevenueCats = fetch(
          buildApiUrl(apiEndpoint, entityRevenueCatSearchString, encodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchHighestPaidByEntity = fetch(
          buildApiUrl(apiEndpoint, highestPaidEmployeesByEntitySearchString, highestPaidEncodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchComplianceData = fetch(
          buildApiUrl(apiEndpoint, complianceDataSearchString, encodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);
        
        const fetchVendorData = fetch(
          buildApiUrl(apiEndpoint, vendorDataSearchString, vendorSearchEncodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchBatchListData = fetch(
          buildApiUrl(apiEndpoint, batchListSearchString, encodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchReportDocs = fetch(
          buildApiUrl(apiEndpoint, reportDocsSearchString, encodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchEntityDataQualityData = fetch(
          buildApiUrl(apiEndpoint, dataQualitySearchString, vendorSearchEncodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchEntityDetails = fetch(
          buildApiUrl(apiEndpoint, entityDetailsSearchString, encodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchEntityPopulation = fetch(
          buildApiUrl(apiEndpoint, entityPopulationSearchString, vendorSearchEncodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchTotalRevenueLong = fetch(
          buildApiUrl(apiEndpoint, totalRevenueLongSearchString, vendorSearchEncodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchTotalExpenditureLong = fetch(
          buildApiUrl(apiEndpoint, totalExpenditureLongSearchString, vendorSearchEncodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const fetchUnrestrictedCashLong = fetch(
          buildApiUrl(apiEndpoint, unrestrictedCashLongSearchString, vendorSearchEncodedParam),
          { signal }
        ).then(res => res.ok ? res.json() : []);

        const [
          sd,
          fundData,
          orgData,
          catData,
          revenueFund,
          revenueOrg,
          revenueCat,
          highestPaidByEntity,
          complianceDataFromAPI,
          vendorSearch,
          batchListData,
          reportDocsFromApi,
          entityDataQuality,
          entityDetailsDataFromApi,
          entityPopulation,
          totalRevenueLong,
          totalExpenditureLong,
          unrestrictedCashLong,
        ] = await Promise.all([
          fetchSurpDef,
          fetchExpensesFund,
          fetchExpensesOrg,
          fetchExpensesCat,
          fetchRevenueFunds,
          fetchRevenueOrgs,
          fetchRevenueCats,
          fetchHighestPaidByEntity,
          fetchComplianceData,
          fetchVendorData,
          fetchBatchListData,
          fetchReportDocs,
          fetchEntityDataQualityData,
          fetchEntityDetails,
          fetchEntityPopulation,
          fetchTotalRevenueLong,
          fetchTotalExpenditureLong,
          fetchUnrestrictedCashLong
          
        ]);

        if (!signal.aborted) {
          setSurpDefData(Array.isArray(sd) ? sd : [sd]);
          setExpensesFundData(Array.isArray(fundData) ? fundData : [fundData]);
          setExpensesOrgData(Array.isArray(orgData) ? orgData : [orgData]);
          setExpensesCatData(Array.isArray(catData) ? catData : [catData]);
          setRevenueFundData(Array.isArray(revenueFund) ? revenueFund : [revenueFund]);
          setRevenueOrgData(Array.isArray(revenueOrg) ? revenueOrg : [revenueOrg]);
          setRevenueCatData(Array.isArray(revenueCat) ? revenueCat : [revenueCat]);
          setHighestPaidData(Array.isArray(highestPaidByEntity) ? highestPaidByEntity : [highestPaidByEntity]);
          setReportDocs(Array.isArray(reportDocsFromApi) ? reportDocsFromApi : []);
          setEntityDetailsData(Array.isArray(entityDetailsDataFromApi) ? entityDetailsDataFromApi : []);
          setEntityOverviewHeaderData(Array.isArray(entityDetailsDataFromApi) ? entityDetailsDataFromApi : []);
          const formattedData = Array.isArray(vendorSearch) 
            ? vendorSearch.map((item: any) => ({
                ...item,
                vendor_name: toTitleCase(item.vendor_name)
              }))
            : [];
          setVendorSearchData(formattedData);
          setBatchSummaryData(Array.isArray(batchListData) ? batchListData : [batchListData]);
          setEntityDataQualityData(Array.isArray(entityDataQuality) ? entityDataQuality : [entityDataQuality]);
          setEntityPopulationData(Array.isArray(entityPopulation) ? entityPopulation : [entityPopulation]);
          setTotalRevenueLongData(Array.isArray(totalRevenueLong) ? totalRevenueLong : [totalRevenueLong]);
          setTotalExpenditureLongData(Array.isArray(totalExpenditureLong) ? totalExpenditureLong : [totalExpenditureLong]);
          setUnrestrictedCashLongData(Array.isArray(unrestrictedCashLong) ? unrestrictedCashLong : [unrestrictedCashLong]);

          const normalizedComplianceData: ComplianceReportApi[] = Array.isArray(complianceDataFromAPI)
            ? complianceDataFromAPI.map((d: any) => ({
                entity_id: d.entity_id,
                legacy_name: d.legacy_name ?? '',
                sf_name: d.sf_name ?? '',
                legacy_govt_lvl: d.legacy_govt_lvl ?? '',
                govt_lvl: d.govt_lvl ?? '',
                active: d.active ?? '',
                sf_id: d.sf_id ?? 0,
                tax_holding_county: d.tax_holding_county ?? '',
                entity_status: d.entity_status ?? '',
                auto_transparency_hold: d.auto_transparency_hold ?? '',
               rvex_beg: d.rvex_beg ?? d.rvex_beg_date ?? d.rvex_beg_string ?? '',
               py_beg: d.py_beg ?? d.py_beg_date ?? d.py_beg_string ?? '',
               fy_end: d.fy_end ?? '',
               miss_lg: d.miss_lg ?? null,
               miss_dandi: d.miss_dandi ?? null,
                miss_rev: d.miss_rev ?? null,
                miss_exp: d.miss_exp ?? null,
                miss_pay: d.miss_pay ?? null,
            })) : [];

          setComplianceData(normalizedComplianceData);

          const cleanedEntityDetailsData: EntityDetailsInterface[] = (Array.isArray(entityDetailsDataFromApi) ? entityDetailsDataFromApi : [entityDetailsDataFromApi]).map((d: any) => (
          {
            entity_id: d.entity_id ? d.entity_id : 'Missing',
            entity_name: d.entity_name ? d.entity_name : 'Missing',
            entity_government_type : String(d.entity_government_type), 
            entity_government_class: String(d.entity_government_class), 
            entity_name_snakecase: String(d.entity_name_snakecase) , 
            sf_name: String(d.sf_name) , 
            sf_id: String(d.sf_id) , 
            tax_commission_id: String(d.tax_commission_id) , 
            ein: String(d.ein) , 
            finet_vendor_ids: String(d.finet_vendor_ids) , 
            transparency_id: String(d.transparency_id) , 

            sf_reports_to_transparent_utah : Boolean(d.sf_reports_to_transparent_utah), 
            sf_reports_to_privacy: Boolean(d.sf_reports_to_privacy), 
            sf_reports_to_local_gov: Boolean(d.sf_reports_to_local_gov), 
            sf_reports_to_treasurer: Boolean(d.sf_reports_to_treasurer), 

            public_infrastructure_district: Boolean(d.public_infrastructure_district), 
            infrastructure_financing_district: Boolean(d.infrastructure_financing_district), 
            special_project_entity: Boolean(d.special_project_entity), 
            governmental_non_profits: Boolean(d.governmental_non_profits), 
            fs_reports_with_parent: Boolean(d.fs_reports_with_parent), 
            fee_assessing_entity: Boolean(d.fee_assessing_entity), 
            component_unit_type: Boolean(d.component_unit_type), 
            tax_holding_county: Boolean(d.tax_holding_county), 

            created_in_salesforce_date: d.created_in_salesforce_date ? d.created_in_salesforce_date : 'Missing', 
            creation_date: d.creation_date ? d.creation_date : 'Missing', 

            website: d.website ? d.website : "Missing", 
            entity_description: d.entity_description ? d.entity_description : "Missing", 
            entity_purpose: d.entity_purpose ? d.entity_purpose : "Missing", 

            form_of_government: d.form_of_government ? d.form_of_government : "Missing",  
            number_of_governing_body_seats: d.number_of_governing_body_seats ? d.number_of_governing_body_seats : "Missing", 
            external_auditors: d.external_auditors ? d.external_auditors : "Missing", 

            billing_address: d.billing_address ? d.billing_address : "Missing", 
            billing_street:   d.billing_street ? d.billing_street : "Missing", 
            billing_city: d.billing_city ? d.billing_city : "Missing", 
            billing_country:  d.billing_country ? d.billing_country : "Missing", 
            billing_postal_code: d.billing_postal_code ? d.billing_postal_code : "Missing", 
            billing_state: d.billing_state ? d.billing_state : "Missing", 

            shipping_address: d.shipping_address ? d.shipping_address : "Missing", 
            shipping_street: d.shipping_street ? d.shipping_street : "Missing", 
            shipping_city: d.shipping_city ? d.shipping_city : "Missing", 
            shipping_postal_code: d.shipping_postal_code ? d.shipping_postal_code : "Missing", 
            shipping_state: d.shipping_state ? d.shipping_state : "Missing", 
            shipping_country: d.shipping_country ? d.shipping_country : "Missing", 
            
            fiscal_year_begins : d.fiscal_year_begins ? d.fiscal_year_begins : 'Missing', 
            fiscal_year_end : d.fiscal_year_end ? d.fiscal_year_end : 'Missing',  
            fiscal_year_change_effective_date : d.fiscal_year_change_effective_date ? d.fiscal_year_change_effective_date : 'Missing', 
            fiscal_year_has_changed : d.fiscal_year_has_changed ? d.fiscal_year_has_changed : 'Missing', 
            transparency_report_expense_revenue_start_date : d.transparency_report_expense_revenue_start_date ? d.transparency_report_expense_revenue_start_date : 'Current',  
            transparency_report_expense_revenue_end_date : d.transparency_report_expense_revenue_end_date ? d.transparency_report_expense_revenue_end_date : 'Current', 
            transparency_report_wage_start_date : d.transparency_report_wage_start_date ? d.transparency_report_wage_start_date : 'Current', 
            transparency_report_wage_end_date : d.transparency_report_wage_end_date ? d.transparency_report_wage_end_date : 'Current',  

            entity_status: d.entity_status ? d.entity_status : "Missing", 
            hold_funds: Boolean(d.hold_funds), 
            hold_deposit_and_investment: Boolean(d.hold_deposit_and_investment), 
            suspend_funds: Boolean(d.suspend_funds), 
            suspend_funds_deposit_and_investment: Boolean(d.suspend_funds_deposit_and_investment), 
            freeze_funds: Boolean(d.freeze_funds), 
            freeze_funds_deposit_and_investment: Boolean(d.freeze_funds_deposit_and_investment), 
            transparency_delinquency: Boolean(d.transparency_delinquency), 
            transparency_hold: Boolean(d.transparency_hold), 
            transparency_suspend: Boolean(d.transparency_suspend)
          }
          ));

          setEntityDetailsDataClean(cleanedEntityDetailsData);

          const cats = Array.from(
            new Set((Array.isArray(fundData) ? fundData : [fundData])
              .filter((e: any) => e.agg1 !== 'NOT SPECIFIED')
              .map((e: any) => e.agg1))
          );
          setCategories(cats);
        }

      } catch (err: any) {
        if (err.name !== 'AbortError') console.error('Fetch error:', err);
      } finally {
        setLoading(false);
        setLoadingSurpDef(false);
        setLoadingExpenses(false);
        setLoadingRevenue(false);
        setLoadingHighestPaid(false);
        setLoadingCompliance(false);
        setLoadingVendorSearch(false);
        setLoadingReports(false);
        setLoadingEntityDetails(false);
        setEntityHeaderLoading(false); // Added to get entity header updated correctly
        setLoadingRevenueTrends(false);
        setLoadingExpenditureTrends(false);
        setLoadingCashTrends(false);
      }
    };

    fetchEntitiesAndAllData();
    return () => controller.abort();
  }, [selectedEntityId, fiscalYear]);

  useEffect(() => {
    if (!allEntities.length) return;
    const selected = allEntities.find(e => e.id === selectedEntityId);
    setEntity(selected ? selected.name : null);
  }, [selectedEntityId, allEntities]);

  
  useEffect(() => {
    if (!allEntities.length) return;     // wait until the entity list has loaded
    if (selectedEntityId) return;        // don't override an existing selection

    const param = searchParams.get('entity');
    if (!param) return;

    const target = decodeURIComponent(param).trim().toLowerCase();

    // Short-code aliases for press-friendly URLs
    const aliases: Record<string, string> = {
      'mida': 'military installation development authority',
    };

    const resolvedTarget = aliases[target] ?? target;

    const match =
      allEntities.find(e => e.name.toLowerCase() === resolvedTarget) ||                          // exact match (alias-resolved or full name)
      allEntities.find(e => new RegExp(`\\b${resolvedTarget}\\b`, 'i').test(e.name)) ||         // whole-word match
      allEntities.find(e => e.name.toLowerCase().includes(resolvedTarget));                     // last-resort substring

    if (match) {
      setSelectedEntities([match.name]);
      setSelectedEntityId(match.id);
    }
  }, [allEntities, searchParams, selectedEntityId]);

  // Added to reset fiscal year when entity changes
  useEffect(() => {
    if (!selectedEntityId) return;
    setFiscalYear(currentYear.toString());
    setHighestPaidData([]);
  }, [selectedEntityId]);

  const handleFiscalYearChange = (newYear: string | null) => {
    setHighestPaidData([]);
    setFiscalYear(newYear);
  }

  interface NavTab { key: string; label: string; icon: string; desc: string; }
  interface NavGroup { key: string; label: string; icon: string; tabs: NavTab[]; }

  const NAV_GROUPS: NavGroup[] = [
    {
      key: 'overview', label: 'Overview', icon: 'fas fa-chart-pie',
      tabs: [{ key: 'overview', label: 'Overview', icon: 'fas fa-chart-pie', desc: 'Entity details' }],
    },
    {
      key: 'financialReports', label: 'Financial Reports', icon: 'fas fa-file-alt',
      tabs: [
        { key: 'reports',           label: 'Reports',              icon: 'fas fa-file-alt',             desc: 'Annual documents' },
        { key: 'revenueTrends',     label: 'Revenue Trends',       icon: 'fas fa-chart-bar',            desc: 'Year-over-year' },
        { key: 'expenditureTrends', label: 'Expenditure Trends',   icon: 'fas fa-file-invoice-dollar',  desc: '6-year comparisons & per capita' },
        { key: 'cashTrends',        label: 'Cash Reserves',        icon: 'fas fa-piggy-bank',           desc: 'Unrestricted cash & fiscal health' },
      ],
    },
    {
      key: 'generalLedger', label: 'General Ledger Data', icon: 'fas fa-database',
      tabs: [
        { key: 'batchList',        label: 'Transparency Reports', icon: 'fas fa-layer-group',      desc: 'Batch uploads' },
        { key: 'complianceReport', label: 'Compliance Report',    icon: 'fas fa-clipboard-check',  desc: 'Audit status & findings' },
        { key: 'dataQuality',     label: 'Data Quality',         icon: 'fas fa-shield-alt',       desc: 'Completeness scores' },
        { key: 'revenue',         label: 'Revenue Analysis',     icon: 'fas fa-coins',            desc: 'Sources & breakdown' },
        { key: 'expense',         label: 'Expense Analysis',     icon: 'fas fa-receipt',          desc: 'By category & fund' },
        { key: 'surpdef',         label: 'Surplus / Deficit',    icon: 'fas fa-balance-scale',    desc: 'Net position by year' },
        { key: 'highestPaid',     label: 'Highest Paid Employees', icon: 'fas fa-user-tie',       desc: 'Top compensation' },
        { key: 'vendorSearch',    label: 'Vendor Payments',      icon: 'fas fa-hand-holding-usd', desc: 'All transactions' },
      ],
    },
    {
      key: 'demographics', label: 'Demographics', icon: 'fas fa-users',
      tabs: [{ key: 'population', label: 'Population', icon: 'fas fa-users', desc: 'Census & demographics' }],
    },
  ];

  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const activeGroup = NAV_GROUPS.find(g => g.tabs.some(t => t.key === activeTab))?.key ?? 'overview';

  const handleGroupClick = (group: NavGroup) => {
    if (group.tabs.length === 1) {
      setActiveTab(group.tabs[0].key);
      setOpenGroup(null);
      return;
    }
    setOpenGroup(prev => (prev === group.key ? null : group.key));
  };

  const handleSubTabClick = (tabKey: string) => {
    setActiveTab(tabKey);
    setOpenGroup(null);
  };

  const entityHeader = entityOverviewHeaderData?.[0] as any;
  const latestPop = entityPopulationData
    ?.slice()
    .sort((a, b) => b.census_year - a.census_year)?.[0];

  const latestPerCapitaSpending = totalRevenueLongData
    ?.slice()
    .filter((d: any) => d.per_capita_metric != null && !isNaN(d.per_capita_metric))
    .sort((a: any, b: any) => b.report_year - a.report_year)?.[0] as any;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <EntityDetailsOverview
            loading={entityHeaderLoading}
            entityDetailsData={entityDetailsDataClean}
          />
        );
                 
      case 'surpdef':
          return <EntitySurpDefOverview 
                  data={surpDefData} 
                  />
      case 'highestPaid':
          return <EntityHighestPaidChartAndTable
                    key={selectedEntityId}
                    selectedEntityId={selectedEntityId ?? null}
                    formatCurrency={formatCurrency}
                    formatEntityString={formatEntityString}
                    data={highestPaidData}
                    loading={loadingHighestPaid}
                    activeTab={activeTab}
                    entityName={entity ?? undefined}
                    fiscalYear={fiscalYear ?? currentYear.toString()}
                    onFiscalYearChange={handleFiscalYearChange}
                  />

      case 'expense':
          return <>
                    <EntityExpensesChartAndTable
                      chartLevel="category"
                      selectedEntityId={selectedEntityId}
                      formatCurrency={formatCurrency}
                      formatEntityString={formatEntityString}
                      data={expensesCatData}
                      loading={loadingExpenses}
                      activeTab={activeTab}
                    />

                    <EntityExpensesChartAndTable
                      chartLevel="organization"
                      selectedEntityId={selectedEntityId}
                      formatCurrency={formatCurrency}
                      formatEntityString={formatEntityString}
                      data={expensesOrgData}
                      loading={loadingExpenses}
                      activeTab={activeTab}
                    />

                    <EntityExpensesChartAndTable
                      chartLevel="fund"
                      selectedEntityId={selectedEntityId}
                      formatCurrency={formatCurrency}
                      formatEntityString={formatEntityString}
                      data={expensesFundData}
                      loading={loadingExpenses}
                      activeTab={activeTab}
                    />
                  </>

      case 'revenue':
          return <>
                    <EntityRevenueChartAndTable
                      chartLevel="category"
                      formatCurrency={formatCurrency}
                      formatEntityString={formatEntityString}
                      data={revenueCatData}
                      selectedEntityId={selectedEntityId}
                      activeTab={activeTab}
                    />

                    <EntityRevenueChartAndTable
                      chartLevel="organization"
                      formatCurrency={formatCurrency}
                      formatEntityString={formatEntityString}
                      data={revenueOrgData}
                      selectedEntityId={selectedEntityId}
                      activeTab={activeTab}
                    />

                    <EntityRevenueChartAndTable
                      chartLevel="fund"
                      formatCurrency={formatCurrency}
                      formatEntityString={formatEntityString}
                      data={revenueFundData}
                      selectedEntityId={selectedEntityId}
                      activeTab={activeTab}
                    />  
                </>

      case 'complianceReport':
          return <EntityComplianceReportOverview
                  entityComplianceData={complianceData}
                  parentEntityName={entity ?? undefined}
                  parentEntityId={selectedEntityId}
                  />

      case 'vendorSearch':
          return <EntityVendorHighestPaidOverview
                    highestPaidVendorData={vendorSearchData}
                    parentEntityName={entity ?? undefined}
                    parentEntityId={selectedEntityId}
                    formatCurrency={formatCurrency}
                    formatEntityString={formatEntityString}
                  />

      case 'batchList':
          return <EntityBatchListOverview
                    batchSummaryData={batchSummaryData}
                    parentEntityName={entity ?? undefined}
                    parentEntityId={selectedEntityId}
                    formatCurrency={formatCurrency}
                    formatEntityString={formatEntityString}
                  />
                  
      case 'dataQuality':
          return <EntityDataQualityOverview
                    entityDataQualityScoreData={entityDataQualityData}
                    parentEntityName={entity ?? undefined}
                    parentEntityId={selectedEntityId}
                    formatCurrency={formatCurrency}
                    formatEntityString={formatEntityString}
                    loading={loading}
                    activeTab={activeTab}
                  />

      case 'reports':
            return (
               <EntityReportsOverview
                   loading={loadingReports}
                   reportDocs={reportDocs}
                  />
        );

      case 'population':
            return (
              <EntityPopulationOverview
                entityPopulationData={entityPopulationData}
                parentEntityName={entity ?? undefined}
              />
            );
      
      case 'revenueTrends':
          return (
            <EntityTotalRevenueOverview
              data={totalRevenueLongData}
              loading={loadingRevenueTrends}
              parentEntityName={entity ?? undefined}
              parentEntityId={selectedEntityId}
              formatCurrency={formatCurrency}
              formatEntityString={formatEntityString}
              activeTab={activeTab}
              entityPopulationData={entityPopulationData}
            />
         )

      case 'expenditureTrends':
          return (
            <EntityTotalExpenditureOverview
              data={totalExpenditureLongData}
              loading={loadingExpenditureTrends}
              parentEntityName={entity ?? undefined}
              parentEntityId={selectedEntityId}
              formatCurrency={formatCurrency}
              formatEntityString={formatEntityString}
              activeTab={activeTab}
              entityPopulationData={entityPopulationData}
            />
         )

      case 'cashTrends':
          return (
            <EntityUnrestrictedCashOverview
              data={unrestrictedCashLongData}
              loading={loadingCashTrends}
              parentEntityName={entity ?? undefined}
              parentEntityId={selectedEntityId}
              formatCurrency={formatCurrency}
              formatEntityString={formatEntityString}
              activeTab={activeTab}
              entityPopulationData={entityPopulationData}
              totalExpenditureLongData={totalExpenditureLongData}
            />
         )

      default:
    }
  };

  return (
    <div className="dashboard">
      <FeatureNav/>
      <BreadCrumbs/>

      <div className="tu-entityDash__hero">
        <Container>
          <div className="tu-entityDash__heroCard">
            <h1 className="tu-entityDash__title">Entity Overview Dashboard</h1>
            <p className="tu-entityDash__subtitle">
              Explore financial data, compliance reports, and public records for Utah government entities. Select an entity below to get started.
            </p>
            <div className="tu-entityDash__selectorWrap">
              <label className="tu-entityDash__selectLabel">
                <i className="fas fa-landmark" aria-hidden="true"></i>
                &nbsp;&nbsp;Select an Entity
              </label>
              <SingleSelectAsync
                value={selectedEntities.length > 0 ? { value: selectedEntities[0], label: selectedEntities[0] } : null}
                onChange={(selectedOption) => {
                  const entityMatch = allEntities.find((e) => e.name === selectedOption?.value);
                  setSelectedEntities(selectedOption ? [selectedOption.value] : []);
                  setSelectedEntityId(entityMatch?.id ?? 0);
                }}
                isSearchable
                loadOptions={async (inputValue: string) => {
                  return allEntities
                    .filter((e) => e.name.toLowerCase().includes(inputValue.toLowerCase()))
                    .map((e) => ({ value: e.name, label: e.name }));
                }}
                defaultOptions={allEntities.map((e) => ({ value: e.name, label: e.name }))}
              />
            </div>
          </div>

          <div className="tu-entityDash__navWrap">
            {entity && (
              <div className="tu-entityDash__infoBar">
                {loadingEntityDetails ? (
                  <div className="tu-entityDash__loading">
                    <div className="tu-entityDash__spinner"></div>
                    <span>Loading entity data...</span>
                  </div>
                ) : (
                  <div className="tu-entityDash__infoRow">
                    <h2 className="tu-entityDash__name">{entity}</h2>
                    <div className="tu-entityDash__meta">
                      {entityHeader?.entity_government_type && (
                        <span className="tu-entityDash__badge">
                          <i className="fas fa-building" aria-hidden="true"></i>
                          &nbsp;{entityHeader.entity_government_type}
                          {entityHeader.entity_government_class ? ` · ${entityHeader.entity_government_class}` : ''}
                        </span>
                      )}
                      {entityHeader?.website && entityHeader.website !== 'Missing' && (
                        <a
                          href={entityHeader.website.startsWith('http') ? entityHeader.website : `http://${entityHeader.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tu-entityDash__link"
                        >
                          <i className="fas fa-globe" aria-hidden="true"></i>
                          &nbsp;{entityHeader.website.replace(/^https?:\/\//, '')}
                        </a>
                      )}
                      {latestPop?.census_population && (
                        <span className="tu-entityDash__stat">
                          <i className="fas fa-users" aria-hidden="true"></i>
                          &nbsp;Pop. {latestPop.census_population.toLocaleString()}
                        </span>
                      )}
                      {latestPerCapitaSpending?.per_capita_metric ? (
                        <span className="tu-entityDash__stat">
                          <i className="fas fa-arrow-down" aria-hidden="true"></i>
                          &nbsp;Per Capita Spending: {formatCurrency(latestPerCapitaSpending.per_capita_metric)}
                        </span>
                      ) : (
                        <span className="tu-entityDash__comingSoon">
                          <i className="fas fa-arrow-down" aria-hidden="true"></i>
                          &nbsp;Per Capita Spending
                          <span className="tu-entityDash__comingSoonTag">coming soon</span>
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="tu-entityNav">
              <div className="tu-entityNav__categories">
                {NAV_GROUPS.map(group => {
                  const isActive = activeGroup === group.key;
                  const isOpen = openGroup === group.key;
                  const hasDropdown = group.tabs.length > 1;
                  return (
                    <button
                      key={group.key}
                      className={
                        'tu-entityNav__cat'
                        + (hasDropdown ? ' tu-entityNav__cat--hasDropdown' : '')
                        + (isActive ? ' tu-entityNav__cat--active' : '')
                        + (isOpen ? ' tu-entityNav__cat--open' : '')
                      }
                      onClick={() => handleGroupClick(group)}
                      type="button"
                    >
                      <i className={group.icon + ' tu-entityNav__catIcon'} aria-hidden="true"></i>
                      <span className="tu-entityNav__catLabel">{group.label}</span>
                      {hasDropdown && (
                        <i
                          className={'fas fa-chevron-down tu-entityNav__chevron' + (isOpen ? ' tu-entityNav__chevron--open' : '')}
                          aria-hidden="true"
                        ></i>
                      )}
                    </button>
                  );
                })}
              </div>

              {NAV_GROUPS.filter(g => g.tabs.length > 1).map(group => (
                <div
                  key={group.key}
                  className={'tu-entityNav__panel' + (openGroup === group.key ? ' tu-entityNav__panel--open' : '')}
                >
                  <div className="tu-entityNav__subTabs">
                    {group.tabs.map(tab => (
                      <button
                        key={tab.key}
                        className={'tu-entityNav__sub' + (activeTab === tab.key ? ' tu-entityNav__sub--active' : '')}
                        onClick={() => handleSubTabClick(tab.key)}
                        type="button"
                      >
                        <div className="tu-entityNav__subIcon">
                          <i className={tab.icon} aria-hidden="true"></i>
                        </div>
                        <div className="tu-entityNav__subText">
                          <span className="tu-entityNav__subLabel">{tab.label}</span>
                          <span className="tu-entityNav__subDesc">{tab.desc}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </div>

      {/* Main Content */}
      <Container className="mt-4 mb-5">
        {renderTabContent()}
      </Container>
      <FooterSection />
    </div>
  )
}

export default EntityOverviewDashboard;